import React, { FC } from 'react';
import { Card, Paragraph, Heading, Tag } from '@udir-design/react/beta';
import { getLanguage } from '../../../utils/searchHelpers';
import { getContentType, getLastModified } from '../../../utils/resultHelpers';

export interface ILaereplanResultCardProps {
    /** Result object — must have læreplan-relevant fields */
    result: {
        title: string;
        status?: string;
        innholdstype?: string;
        fltypefilter?: string;
        kode?: string;
        fagnavn?: string;
        laereplannavn?: string;
        datoendret?: string;
        language?: string;
        /** ILPVResult uses Path, IGlobalResult uses Url */
        Path?: string;
        Url?: string;
    };
    idx: number;
}

export const getLaereplanTitleText = (result: ILaereplanResultCardProps['result']): string => {
    if (result.fltypefilter === "Læreplan" && result.laereplannavn) {
        let laereplannavn = result.laereplannavn;
        if (laereplannavn.indexOf("Læreplan for") !== -1) {
            laereplannavn = laereplannavn.replace("Læreplan for ", "");
            laereplannavn = laereplannavn.trim().charAt(0).toUpperCase() + laereplannavn.slice(1);
        } else if (laereplannavn.indexOf("Læreplan i") !== -1) {
            laereplannavn = laereplannavn.replace('Læreplan i ', '');
            laereplannavn = laereplannavn.charAt(0).toUpperCase() + laereplannavn.slice(1);
        }
        return result.kode ? `${laereplannavn} (${result.kode})` : laereplannavn;
    } else if ((result.innholdstype === "Fagkode" || result.fltypefilter === "Fagkode") && result.kode && result.fagnavn) {
        return `${result.kode} - ${result.fagnavn}`;
    } else if (result.fltypefilter === "Overordnet del kapittel") {
        return `${result.title} - Overordnet del av læreplanverket`;
    }
    return result.title;
};

const getStatusColor = (status: string): 'warning' | 'info' | 'accent' => {
    if (status === 'Utgått' || status === 'Utgått (Kunnskapsløftet 2020)' || status === 'Utgått (Kunnskapsløftet 2006)') {
        return 'warning';
    }
    if (status === 'Kommende') {
        return 'info';
    }
    return 'accent';
};

const LaereplanResultCard: FC<ILaereplanResultCardProps> = ({ result, idx }) => {
    const language_attr = getLanguage(result);
    const dateFormatted = getLastModified(result);
    const laereplanTitle = getLaereplanTitleText(result);
    // ILPVResult has Path, IGlobalResult has Url
    const rawUrl = result.Path || result.Url || '';
    const laereplanUrl = rawUrl.replace('/Hele/Komplett_visning', '');

    return (
        <Card className="ResultLinkCard" key={`resultDisplay-${idx}`}>
            <Card.Block className="LinkCard__container">
                <div className="LinkCard">
                    <Paragraph className="LinkCard__headerandsubtitle" data-size="sm">
                        <span className="subtitle-top title">{getContentType(result)}</span>
                    </Paragraph>

                    <Heading level={2} className="headerandbuttons" {...language_attr}>
                        <a className="cardtitle" href={laereplanUrl} rel="noopener noreferrer">
                            {laereplanTitle}
                        </a>
                    </Heading>

                    <div className="resultSubheader">
                        {result.status && result.status.trim().length > 0 && result.status !== 'Gjeldende' && (
                            <Tag data-color={getStatusColor(result.status)}>
                                {result.status}
                            </Tag>
                        )}
                    </div>
                </div>
            </Card.Block>
        </Card>
    );
};

export default LaereplanResultCard;
